/*
 * Вкладка «Метрики»: графики из Prometheus кластера (плагин Roadie) и алерты (AlertsCard,
 * lib/alerts) для кластера, хранилища (Kafka, PostgreSQL, MongoDB, RabbitMQ, Valkey), сервиса,
 * Airflow и модели.
 * Запросы PromQL собираются здесь по типу сущности и её имени, а не пишутся в аннотации:
 * в аннотации prometheus.io/rule нельзя использовать запятую и «|», то есть ни одного
 * матчера с двумя метками.
 *
 * Плагин графиков берёт инстанс Prometheus из аннотации prometheus.io/service-name — она
 * подставляется через EntityProvider из выбранного кластера сущности. Кластеров может быть
 * несколько (entityClusters: окружения сервиса и модели в разных кластерах) — тогда сверху
 * выпадающий список, выбор хранится в параметре ?cluster= адреса; графики и алерты — из
 * Prometheus выбранного кластера. Инстансы и proxy `/prometheus/<кластер>/api` ведёт модуль
 * argocdInstances бэкенда.
 */
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import { EntityProvider, useEntity } from '@backstage/plugin-catalog-react';
import { PrometheusGraph } from '@roadiehq/backstage-plugin-prometheus';
import { useSearchParams } from 'react-router-dom';
import { alertMatcherFor } from '../../lib/alerts';
import { AlertsCard } from './AlertsCard';
import {
  AIRFLOW_TYPE,
  BROKER_TYPE,
  DATABASE_TYPE,
  EntityCluster,
  GITLAB_TYPE,
  KUBERNETES_CLUSTER_TYPE,
  RUSTFS_TYPE,
  ML_MODEL_TYPE,
  MLFLOW_MODEL_NAME_ANNOTATION,
  MONGODB_TYPE,
  RABBITMQ_TYPE,
  SERVICE_TYPE,
  VALKEY_TYPE,
  annotation,
  datastoreNameOf,
  entityClusters,
} from '../../lib/viva';

const DATASTORES_NS = 'datastores';
const RANGE = { hours: 3 };
const STEP = 60;

type GraphSpec = {
  title: string;
  query: string;
  dimension?: string;
  graphType?: 'line' | 'area';
};

/** Основной кластер платформы сущности (undefined — у сущности нет метрик, вкладки нет). */
export const metricsClusterOf = (entity: Entity): string | undefined =>
  entityClusters(entity)[0]?.cluster;

const CLUSTER_PARAM = 'cluster';

/** Подпись кластера в списке: имя и окружения сущности в нём. */
const clusterLabel = (c: EntityCluster): string =>
  c.roles.length ? `${c.cluster} (${c.roles.join(', ')})` : c.cluster;

const ClusterSelect = ({
  clusters,
  value,
  onChange,
}: {
  clusters: EntityCluster[];
  value: string;
  onChange: (cluster: string) => void;
}) => (
  <TextField
    select
    id="metrics-cluster"
    label="Кластер"
    variant="outlined"
    size="small"
    value={value}
    onChange={e => onChange(e.target.value as string)}
    helperText="Графики и алерты — из Prometheus выбранного кластера"
    style={{ minWidth: 320 }}
  >
    {clusters.map(c => (
      <MenuItem key={c.cluster} value={c.cluster}>
        {clusterLabel(c)}
      </MenuItem>
    ))}
  </TextField>
);

const kafkaGraphs = (name: string): GraphSpec[] => {
  const sel = `namespace="${DATASTORES_NS}",strimzi_io_cluster="${name}"`;
  return [
    {
      title: 'Сообщений в секунду по брокерам',
      query: `sum by (pod) (rate(kafka_server_brokertopicmetrics_messagesin_total{${sel}}[5m]))`,
      dimension: 'pod',
      graphType: 'area',
    },
    {
      title: 'Трафик, байт/с (in / out)',
      query:
        `label_replace(sum(rate(kafka_server_brokertopicmetrics_bytesin_total{${sel}}[5m])), "direction", "in", "", "")` +
        ` or label_replace(sum(rate(kafka_server_brokertopicmetrics_bytesout_total{${sel}}[5m])), "direction", "out", "", "")`,
      dimension: 'direction',
    },
    {
      title: 'Недореплицированные партиции',
      query: `sum by (pod) (kafka_server_replicamanager_underreplicatedpartitions{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Лаг консьюмер-групп',
      query: `sum by (consumergroup) (kafka_consumergroup_lag{${sel}})`,
      dimension: 'consumergroup',
    },
    {
      title: 'Диск брокеров, доля занятого',
      query:
        `kubelet_volume_stats_used_bytes{namespace="${DATASTORES_NS}",persistentvolumeclaim=~"data-.*-${name}-.*"}` +
        ` / kubelet_volume_stats_capacity_bytes{namespace="${DATASTORES_NS}",persistentvolumeclaim=~"data-.*-${name}-.*"}`,
      dimension: 'persistentvolumeclaim',
    },
    {
      title: 'Память Kafka, байт',
      query: `sum by (pod) (container_memory_working_set_bytes{namespace="${DATASTORES_NS}",container="kafka",pod=~"${name}-.*"})`,
      dimension: 'pod',
    },
  ];
};

const postgresGraphs = (name: string): GraphSpec[] => {
  const sel = `namespace="${DATASTORES_NS}",pod=~"${name}-[0-9]+"`;
  return [
    {
      title: 'Транзакций в секунду',
      query: `sum by (pod) (rate(cnpg_pg_stat_database_xact_commit{${sel}}[5m]))`,
      dimension: 'pod',
      graphType: 'area',
    },
    {
      title: 'Соединения',
      query: `sum by (pod) (cnpg_backends_total{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Лаг реплик, секунд',
      query: `cnpg_pg_replication_lag{${sel}}`,
      dimension: 'pod',
    },
    {
      title: 'Размер баз, байт',
      query: `max by (datname) (cnpg_pg_database_size_bytes{${sel},datname!~"template.*"})`,
      dimension: 'datname',
    },
    {
      title: 'Диск, доля занятого',
      query:
        `kubelet_volume_stats_used_bytes{namespace="${DATASTORES_NS}",persistentvolumeclaim=~"${name}-[0-9]+(-wal)?"}` +
        ` / kubelet_volume_stats_capacity_bytes{namespace="${DATASTORES_NS}",persistentvolumeclaim=~"${name}-[0-9]+(-wal)?"}`,
      dimension: 'persistentvolumeclaim',
    },
    {
      title: 'CPU PostgreSQL, ядер',
      query: `sum by (pod) (rate(container_cpu_usage_seconds_total{${sel},container="postgres"}[5m]))`,
      dimension: 'pod',
    },
  ];
};

/** Подпись ряда для графика из нескольких выражений (dimension s). */
const seriesOf = (expr: string, label: string) =>
  `label_replace(${expr}, "s", "${label}", "", "")`;

/** Доля занятого диска PVC хранилища (имена PVC задают операторы). */
const pvcUsage = (pvc: string): GraphSpec => {
  const sel = `namespace="${DATASTORES_NS}",persistentvolumeclaim=~"${pvc}"`;
  return {
    title: 'Диск, доля занятого',
    query: `kubelet_volume_stats_used_bytes{${sel}} / kubelet_volume_stats_capacity_bytes{${sel}}`,
    dimension: 'persistentvolumeclaim',
  };
};

/**
 * Хранилища на операторах: метрики экспортёров (mongodb_exporter, rabbitmq_prometheus,
 * redis_exporter) собирают VMPodScrape чартов gitops с меткой viva_datastore=<имя>.
 */
const mongodbGraphs = (name: string): GraphSpec[] => {
  const sel = `namespace="${DATASTORES_NS}",viva_datastore="${name}"`;
  return [
    {
      title: 'Операций в секунду',
      query: `sum by (type) (rate(mongodb_op_counters_total{${sel}}[5m]))`,
      dimension: 'type',
      graphType: 'area',
    },
    {
      title: 'Соединения',
      query: `sum by (pod) (mongodb_ss_connections{${sel},conn_type="current"})`,
      dimension: 'pod',
    },
    {
      title: 'Лаг реплик, секунд',
      query: `max by (pod) (mongodb_mongod_replset_member_replication_lag{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Размер баз, байт',
      query: `max by (database) (mongodb_dbstats_dataSize{${sel},database!~"admin|config|local"})`,
      dimension: 'database',
    },
    pvcUsage(`mongod-data-${name}-rs0-[0-9]+`),
    {
      title: 'CPU MongoDB, ядер',
      query: `sum by (pod) (rate(container_cpu_usage_seconds_total{namespace="${DATASTORES_NS}",pod=~"${name}-rs0-[0-9]+",container="mongod"}[5m]))`,
      dimension: 'pod',
    },
  ];
};

const rabbitmqGraphs = (name: string): GraphSpec[] => {
  const sel = `namespace="${DATASTORES_NS}",viva_datastore="${name}"`;
  return [
    {
      title: 'Сообщений в секунду',
      query: `${seriesOf(`sum(rate(rabbitmq_global_messages_received_total{${sel}}[5m]))`, 'опубликовано')} or ${seriesOf(`sum(rate(rabbitmq_global_messages_delivered_total{${sel}}[5m]))`, 'доставлено')}`,
      dimension: 's',
      graphType: 'area',
    },
    {
      title: 'Сообщений в очередях',
      query: `sum by (queue) (rabbitmq_detailed_queue_messages{${sel}})`,
      dimension: 'queue',
    },
    {
      title: 'Потребители очередей',
      query: `sum by (queue) (rabbitmq_detailed_queue_consumers{${sel}})`,
      dimension: 'queue',
    },
    {
      title: 'Соединения',
      query: `sum by (pod) (rabbitmq_connections{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Память узлов, байт',
      query: `sum by (pod) (rabbitmq_process_resident_memory_bytes{${sel}})`,
      dimension: 'pod',
    },
    pvcUsage(`persistence-${name}-server-[0-9]+`),
  ];
};

const valkeyGraphs = (name: string): GraphSpec[] => {
  const sel = `namespace="${DATASTORES_NS}",viva_datastore="${name}"`;
  return [
    {
      title: 'Команд в секунду',
      query: `sum by (pod) (rate(redis_commands_processed_total{${sel}}[5m]))`,
      dimension: 'pod',
      graphType: 'area',
    },
    {
      title: 'Память, байт',
      query: `sum by (pod) (redis_memory_used_bytes{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Клиенты',
      query: `sum by (pod) (redis_connected_clients{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Ключей',
      query: `sum by (pod) (redis_db_keys{${sel}})`,
      dimension: 'pod',
    },
    {
      title: 'Попадания в кэш, доля',
      query: `sum(rate(redis_keyspace_hits_total{${sel}}[5m])) / clamp_min(sum(rate(redis_keyspace_hits_total{${sel}}[5m])) + sum(rate(redis_keyspace_misses_total{${sel}}[5m])), 0.001)`,
    },
    {
      title: 'Вытеснено и истекло ключей в секунду',
      query: `${seriesOf(`sum(rate(redis_evicted_keys_total{${sel}}[5m]))`, 'вытеснено')} or ${seriesOf(`sum(rate(redis_expired_keys_total{${sel}}[5m]))`, 'истекло')}`,
      dimension: 's',
    },
  ];
};

/** Сервинг модели: Deployment <модель>-predictor в staging и production, трафик через HAProxy. */
const modelGraphs = (name: string): GraphSpec[] => {
  const envs = 'namespace=~"staging|production"';
  // backend'ы HAProxy называются <namespace>_svc_<service>_<port>
  const backend = `proxy=~"(staging|production)_svc_${name}-predictor_.*"`;
  // проверка снаружи: VMProbe чарта model-serving -> blackbox exporter кластера, https://<хост><healthPath>
  const probe = `model="${name}"`;
  return [
    {
      title: 'Доступность снаружи (1 — OK)',
      query: `min by (env, hostname) (probe_success{${probe}})`,
      dimension: 'hostname',
    },
    {
      title: 'Время ответа снаружи, секунд',
      query: `max by (env, hostname) (probe_duration_seconds{${probe}})`,
      dimension: 'hostname',
    },
    {
      title: 'Запросов в секунду (HAProxy)',
      query: `sum by (proxy) (rate(haproxy_backend_http_requests_total{${backend}}[5m]))`,
      dimension: 'proxy',
      graphType: 'area',
    },
    {
      title: 'Ошибки 5xx в секунду',
      query: `sum by (proxy) (rate(haproxy_backend_http_responses_total{code="5xx",${backend}}[5m]))`,
      dimension: 'proxy',
    },
    {
      title: 'Среднее время ответа, секунд',
      query: `haproxy_backend_response_time_average_seconds{${backend}}`,
      dimension: 'proxy',
    },
    {
      title: 'Реплики predictor',
      query: `kube_deployment_status_replicas_available{${envs},deployment=~"${name}-predictor.*"}`,
      dimension: 'namespace',
    },
    {
      title: 'CPU подов, ядер',
      query: `sum by (namespace) (rate(container_cpu_usage_seconds_total{${envs},container="kserve-container",pod=~"${name}-predictor-.*"}[5m]))`,
      dimension: 'namespace',
    },
    {
      title: 'Память подов, байт',
      query: `sum by (namespace) (container_memory_working_set_bytes{${envs},container="kserve-container",pod=~"${name}-predictor-.*"})`,
      dimension: 'namespace',
    },
  ];
};

/** Сервис классической разработки: Deployment <сервис> в dev / stage / prod / feat-*, трафик через HAProxy. */
const serviceGraphs = (name: string): GraphSpec[] => {
  // namespace окружения задаёт джоба деплоя (по умолчанию dev/stage/prod/feat-<ветка>), поэтому
  // по namespace не фильтруем — только по имени сервиса
  const backend = `proxy=~".*_svc_${name}_http"`;
  // проверки доступности снаружи: VMProbe чарта app -> blackbox exporter кластера (метки
  // hostname/path — URL цели); 1 — OK, 0 — ProbeFailed через 5 минут
  const probe = `app="${name}"`;
  return [
    {
      title: 'Доступность снаружи (1 — OK)',
      query: `min by (hostname, path) (probe_success{${probe}})`,
      dimension: 'hostname',
    },
    {
      title: 'Время ответа снаружи, секунд',
      query: `max by (hostname, path) (probe_duration_seconds{${probe}})`,
      dimension: 'hostname',
    },
    {
      title: 'Запросов в секунду (HAProxy)',
      query: `sum by (proxy) (rate(haproxy_backend_http_requests_total{${backend}}[5m]))`,
      dimension: 'proxy',
      graphType: 'area',
    },
    {
      title: 'Ошибки 5xx в секунду',
      query: `sum by (proxy) (rate(haproxy_backend_http_responses_total{code="5xx",${backend}}[5m]))`,
      dimension: 'proxy',
    },
    {
      title: 'Среднее время ответа, секунд',
      query: `haproxy_backend_response_time_average_seconds{${backend}}`,
      dimension: 'proxy',
    },
    {
      title: 'Реплики по окружениям',
      query: `kube_deployment_status_replicas_available{deployment="${name}"}`,
      dimension: 'namespace',
    },
    {
      title: 'CPU подов, ядер',
      query: `sum by (namespace) (rate(container_cpu_usage_seconds_total{container="app",pod=~"${name}-.*"}[5m]))`,
      dimension: 'namespace',
    },
    {
      title: 'Память подов, байт',
      query: `sum by (namespace) (container_memory_working_set_bytes{container="app",pod=~"${name}-.*"})`,
      dimension: 'namespace',
    },
    {
      title: 'Перезапуски контейнеров',
      query: `sum by (namespace) (kube_pod_container_status_restarts_total{container="app",pod=~"${name}-.*"})`,
      dimension: 'namespace',
    },
  ];
};

const clusterGraphs = (): GraphSpec[] => [
  {
    title: 'CPU узлов, доля занятого',
    query: `1 - avg by (instance) (rate(node_cpu_seconds_total{mode="idle"}[5m]))`,
    dimension: 'instance',
    graphType: 'area',
  },
  {
    title: 'Память узлов, доля занятого',
    query: `1 - node_memory_MemAvailable_bytes / node_memory_MemTotal_bytes`,
    dimension: 'instance',
  },
  {
    // backend'ы HAProxy называются <namespace>_svc_<service>_<port>, а хост и путь
    // ingress'а знает kube-state-metrics (kube_ingress_path); порт для джойна ненадёжен
    // (для именованного порта kube_ingress_path пишет 0), поэтому джойн по namespace и
    // сервису. Легенда — «хост/путь».
    title: 'Запросов в секунду через ingress',
    query:
      `label_join(sum by (host, path) (` +
      `sum by (namespace, service_name) (label_replace(label_replace(` +
      `rate(haproxy_backend_http_requests_total[5m]),` +
      ` "namespace", "$1", "proxy", "^([^_]+)_svc_.*"),` +
      ` "service_name", "$1", "proxy", "^[^_]+_svc_(.+)_[^_]+$"))` +
      ` * on (namespace, service_name) group_right ()` +
      ` max by (namespace, service_name, host, path) (kube_ingress_path)` +
      `), "route", "", "host", "path")`,
    dimension: 'route',
  },
  {
    title: 'Дней до истечения сертификатов',
    query: `(certmanager_certificate_expiration_timestamp_seconds - time()) / 86400`,
    dimension: 'name',
  },
  {
    title: 'Диски PVC, доля занятого',
    query: `kubelet_volume_stats_used_bytes / kubelet_volume_stats_capacity_bytes`,
    dimension: 'persistentvolumeclaim',
  },
  {
    title: 'Поды Running по namespace',
    query: `sum by (namespace) (kube_pod_status_phase{phase="Running"})`,
    dimension: 'namespace',
  },
];

/** Инстанс Airflow: метрики statsd-exporter'а чарта (метка airflow_instance — VMServiceScrape). */
const airflowGraphs = (name: string): GraphSpec[] => {
  const sel = `airflow_instance="${name}"`;
  return [
    {
      // VMProbe чарта airflow-instance: API-сервер через внутренний хост ingress (/api/v2/monitor/health)
      title: 'Доступность API-сервера (1 — OK)',
      query: `min(probe_success{${sel}})`,
    },
    {
      title: "Запуски DAG'ов в час: успешные и упавшие",
      query: `label_replace(sum(increase(airflow_dagrun_duration_count{${sel}}[1h])), "state", "success", "", "") or label_replace(sum(increase(airflow_dagrun_failed_count{${sel}}[1h])), "state", "failed", "", "")`,
      dimension: 'state',
      graphType: 'area',
    },
    {
      title: 'Задачи за 5 минут: успешно и упало',
      query: `label_replace(sum(increase(airflow_ti_successes{${sel}}[5m])), "s", "succeeded", "", "") or label_replace(sum(increase(airflow_ti_failures{${sel}}[5m])), "s", "failed", "", "")`,
      dimension: 's',
    },
    {
      title: "Heartbeat scheduler'а (в минуту)",
      query: `sum(increase(airflow_scheduler_heartbeat{${sel}}[1m]))`,
    },
    {
      title: 'Задач в очереди / выполняется',
      query: `label_replace(max(airflow_executor_queued_tasks{${sel}}), "s", "queued", "", "") or label_replace(max(airflow_executor_running_tasks{${sel}}), "s", "running", "", "")`,
      dimension: 's',
    },
  ];
};

/** GitLab платформы: метрики экспортёров Omnibus и проба входа (метка gitlab="platform"). */
const gitlabGraphs = (): GraphSpec[] => {
  const g = 'gitlab="platform"';
  return [
    {
      title: 'Доступность снаружи (1 — OK)',
      query: `min(probe_success{app="gitlab",${g}})`,
    },
    {
      title: 'Экспортёры метрик (1 — отвечает)',
      query: `max by (component) (up{job="gitlab",${g}})`,
      dimension: 'component',
    },
    {
      title: 'Запросов в секунду по кодам (workhorse)',
      query: `sum by (code) (rate(gitlab_workhorse_http_requests_total{${g}}[5m]))`,
      dimension: 'code',
      graphType: 'area',
    },
    {
      title: 'Время ответа p95, секунд (workhorse)',
      query: `histogram_quantile(0.95, sum by (le) (rate(gitlab_workhorse_http_request_duration_seconds_bucket{${g}}[5m])))`,
    },
    {
      title: 'Sidekiq: заданий в секунду',
      query: `label_replace(sum(rate(sidekiq_jobs_processed_total{${g}}[5m])), "s", "выполнено", "", "") or label_replace(sum(rate(sidekiq_jobs_failed_total{${g}}[5m])), "s", "упало", "", "")`,
      dimension: 's',
    },
    {
      title: 'Sidekiq: задержка очередей, секунд (топ-5)',
      query: `topk(5, sidekiq_queue_latency_seconds{${g}})`,
      dimension: 'name',
    },
    {
      title: 'Puma: соединений в очереди',
      query: `max(puma_queued_connections{${g}})`,
    },
    {
      title: 'PostgreSQL: соединения по состоянию',
      query: `sum by (state) (pg_stat_activity_count{${g}})`,
      dimension: 'state',
    },
  ];
};

/**
 * RustFS кластера на хостах (namespace rustfs). Свои метрики RustFS шлёт по OTLP прямо в
 * VictoriaMetrics (rustfs_*; кластерные — ёмкость, диски, erasure set — отдаёт каждый под, отсюда
 * max); доступность — пробы health изнутри кластера и ingress снаружи; поды и узлы —
 * kube-state-metrics и node-exporter (тома local-path статистику kubelet не отдают).
 */
const rustfsGraphs = (): GraphSpec[] => {
  const ns = 'namespace="rustfs"';
  const pods = `${ns},pod=~"rustfs.*"`;
  const series = (expr: string, label: string) =>
    `label_replace(${expr}, "s", "${label}", "", "")`;
  return [
    {
      title: 'Доступность S3 и консоли (1 — OK)',
      query: `min by (instance) (probe_success{job="rustfs-health"} or probe_success{job="blackbox-platform",hostname=~"(rustfs|s3)[.].*"})`,
      dimension: 'instance',
    },
    {
      title: 'Ёмкость, байт: занято и всего',
      query: `${series(
        'max(rustfs_cluster_capacity_used_bytes)',
        'занято',
      )} or ${series(
        'max(rustfs_cluster_capacity_usable_total_bytes)',
        'всего',
      )}`,
      dimension: 's',
    },
    {
      title: 'Объём бакетов, байт',
      query: 'max by (bucket) (rustfs_bucket_usage_usage_bytes)',
      dimension: 'bucket',
      graphType: 'area',
    },
    {
      title: 'Запросы S3 в секунду по операциям',
      query: 'sum by (name) (rate(rustfs_api_requests_total{type="s3"}[5m]))',
      dimension: 'name',
      graphType: 'area',
    },
    {
      title: 'Ошибки HTTP, доля запросов',
      query:
        'sum(rate(rustfs_http_server_failures_total[5m])) / clamp_min(sum(rate(rustfs_http_server_requests_total[5m])), 0.001)',
    },
    {
      title: 'Задержка запросов, секунд (p50, p95)',
      query:
        `${series(
          'histogram_quantile(0.5, sum by (le) (rate(rustfs_http_server_request_duration_seconds_bucket[5m])))',
          'p50',
        )}` +
        ` or ${series(
          'histogram_quantile(0.95, sum by (le) (rate(rustfs_http_server_request_duration_seconds_bucket[5m])))',
          'p95',
        )}`,
      dimension: 's',
    },
    {
      title: 'Трафик S3, байт/с: приём и отдача',
      query:
        `${series(
          'sum(rate(rustfs_http_server_request_body_bytes_total[5m]))',
          'приём',
        )}` +
        ` or ${series(
          'sum(rate(rustfs_http_server_response_body_bytes_total[5m]))',
          'отдача',
        )}`,
      dimension: 's',
      graphType: 'area',
    },
    {
      title: 'Диски: онлайн и офлайн',
      query: `${series(
        'max(rustfs_cluster_health_drives_online_count)',
        'онлайн',
      )} or ${series(
        'max(rustfs_cluster_health_drives_offline_count)',
        'офлайн',
      )}`,
      dimension: 's',
    },
    {
      title: 'Erasure set: запись и чтение (1 — кворум есть)',
      query: `${series(
        'min(rustfs_cluster_erasure_set_write_health)',
        'запись',
      )} or ${series('min(rustfs_cluster_erasure_set_read_health)', 'чтение')}`,
      dimension: 's',
    },
    {
      title: 'Лечение данных: задач в работе',
      query: 'sum(rustfs_heal_task_running)',
    },
    {
      title: 'Готовых подов',
      query: `sum(kube_statefulset_status_replicas_ready{${ns}}) or sum(kube_deployment_status_replicas_available{${ns}})`,
    },
    {
      title: 'Диск узлов с RustFS, доля занятого',
      query:
        `max by (node) ((1 - node_filesystem_avail_bytes{mountpoint="/",fstype!~"tmpfs|overlay"} / node_filesystem_size_bytes{mountpoint="/",fstype!~"tmpfs|overlay"})` +
        ` * on (instance) group_left (node) label_replace(node_uname_info, "node", "$1", "nodename", "(.*)")` +
        ` * on (node) group_left () max by (node) (kube_pod_info{${pods}}))`,
      dimension: 'node',
    },
    {
      title: 'CPU, ядер',
      query: `sum by (pod) (rate(container_cpu_usage_seconds_total{${pods},container="rustfs"}[5m]))`,
      dimension: 'pod',
    },
    {
      title: 'Память, байт',
      query: `sum by (pod) (container_memory_working_set_bytes{${pods},container="rustfs"})`,
      dimension: 'pod',
    },
    {
      title: 'Перезапуски',
      query: `sum by (pod) (kube_pod_container_status_restarts_total{${pods}})`,
      dimension: 'pod',
    },
  ];
};

/** Графики по типу сущности; пусто — сущность без метрик. */
const specsFor = (entity: Entity): { graphs: GraphSpec[] } | undefined => {
  const type = entity.spec?.type;
  const name = entity.metadata.name;
  if (entity.kind === 'Resource' && type === KUBERNETES_CLUSTER_TYPE) {
    return { graphs: clusterGraphs() };
  }
  if (entity.kind === 'Resource' && type === GITLAB_TYPE) {
    return { graphs: gitlabGraphs() };
  }
  if (type === BROKER_TYPE) {
    return { graphs: kafkaGraphs(name.replace(/^kafka-/, '')) };
  }
  if (type === RUSTFS_TYPE) {
    return { graphs: rustfsGraphs() };
  }
  if (type === DATABASE_TYPE) {
    return { graphs: postgresGraphs(name.replace(/^postgres-/, '')) };
  }
  if (type === MONGODB_TYPE) {
    return { graphs: mongodbGraphs(datastoreNameOf(entity)) };
  }
  if (type === RABBITMQ_TYPE) {
    return { graphs: rabbitmqGraphs(datastoreNameOf(entity)) };
  }
  if (type === VALKEY_TYPE) {
    return { graphs: valkeyGraphs(datastoreNameOf(entity)) };
  }
  if (type === SERVICE_TYPE) {
    return { graphs: serviceGraphs(name) };
  }
  if (type === AIRFLOW_TYPE) {
    return { graphs: airflowGraphs(name.replace(/^airflow-/, '')) };
  }
  if (type === ML_MODEL_TYPE) {
    return {
      graphs: modelGraphs(
        annotation(entity, MLFLOW_MODEL_NAME_ANNOTATION) ?? name,
      ),
    };
  }
  return undefined;
};

export const MetricsContent = () => {
  const { entity } = useEntity();
  const clusters = entityClusters(entity);
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(CLUSTER_PARAM);
  // кластер из адреса, если он из списка сущности; иначе основной
  const cluster =
    clusters.find(c => c.cluster === requested)?.cluster ??
    clusters[0]?.cluster;
  const selectCluster = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === clusters[0]?.cluster) params.delete(CLUSTER_PARAM);
    else params.set(CLUSTER_PARAM, next);
    setSearchParams(params, { replace: true });
  };
  const specs = specsFor(entity);
  if (!cluster || !specs) {
    return (
      <EmptyState
        missing="info"
        title="Метрик нет"
        description="У сущности нет привязки к кластеру платформы (аннотация viva.company.com/cluster), поэтому непонятно, в каком Prometheus её искать."
      />
    );
  }
  // Плагин графиков читает инстанс Prometheus из аннотации сущности — подставляем свою
  const withPrometheus: Entity = {
    ...entity,
    metadata: {
      ...entity.metadata,
      annotations: {
        ...entity.metadata.annotations,
        'prometheus.io/service-name': cluster,
      },
    },
  };
  return (
    <EntityProvider entity={withPrometheus}>
      {/* key: смена кластера пересоздаёт графики и карточку — запросы уходят в новый Prometheus */}
      <Grid container spacing={3} key={cluster}>
        {clusters.length > 1 && (
          <Grid item xs={12}>
            <ClusterSelect
              clusters={clusters}
              value={cluster}
              onChange={selectCluster}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <AlertsCard
            cluster={cluster}
            match={alertMatcherFor(entity, cluster)}
          />
        </Grid>
        {specs.graphs.map(g => (
          <Grid item md={6} xs={12} key={g.title}>
            <PrometheusGraph
              title={g.title}
              // плагин подставляет запрос в URL как есть: «+» в регулярках (pod=~"x-[0-9]+")
              // сервер читает как пробел, и график пуст — кодируем сами
              query={encodeURIComponent(g.query)}
              dimension={g.dimension}
              graphType={g.graphType ?? 'line'}
              range={RANGE}
              step={STEP}
            />
          </Grid>
        ))}
      </Grid>
    </EntityProvider>
  );
};
